import { AuditPage } from "@/audit/pages/AuditPage";
import { ChangePasswordPage } from "@/auth/ChangePasswordPage";
import { ForgotPasswordPage } from "@/auth/ForgotPasswordPage";
import { AdminInvitesPage } from "@/auth/AdminInvitesPage";
import { RequireAdmin } from "@/auth/RequireAdmin";
import { RequireAuth } from "@/auth/RequireAuth";
import { RequireRouteAccess } from "@/auth/RequireRouteAccess";
import { LoginPage } from "@/auth/LoginPage";
import { ResetPasswordPage } from "@/auth/ResetPasswordPage";
import { SignupPage } from "@/auth/SignupPage";
import { CompanyPage } from "@/company/pages/CompanyPage";
import { ExploreLayout } from "@/analytics/dashboards/explore/ExploreLayout";
import { ExplorePage } from "@/analytics/dashboards/explore/pages/ExplorePage";
import { GridLayout } from "@/grid/GridLayout";
import { GridPage } from "@/grid/pages/GridPage";
import { DisplayCompanyPage } from "@/display/pages/DisplayCompanyPage";
import { DisplayGridPage } from "@/display/pages/DisplayGridPage";
import { RawLayout } from "@/raw/RawLayout";
import { RawDatasetPage } from "@/raw/pages/RawDatasetPage";
import { defaultRawRoute } from "@/raw/datasets/registry";
import { TargetEditorPage } from "@/targets/pages/TargetEditorPage";
import { TargetLayout } from "@/targets/TargetLayout";
import {
  TeamDashboardIndexPage,
  TeamDashboardPage,
} from "@/team-dashboard/pages/TeamDashboardPage";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

function LegacyTargetsTestRedirect() {
  const { departmentId = "sales" } = useParams<{ departmentId: string }>();
  return <Navigate to={`/targets/${departmentId}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/display" element={<GridLayout />}>
        <Route index element={<Navigate to="/display/sales" replace />} />
        <Route path=":departmentId" element={<DisplayGridPage />} />
        <Route path="company/:departmentId" element={<DisplayCompanyPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<RequireRouteAccess />}>
          <Route path="/" element={<Navigate to="/company/sales" replace />} />

          <Route path="/grid" element={<GridLayout />}>
            <Route index element={<Navigate to="/grid/sales" replace />} />
            <Route path=":departmentId" element={<GridPage />} />
          </Route>

          <Route path="/company" element={<Navigate to="/company/sales" replace />} />
          <Route path="/company/:departmentId" element={<CompanyPage />} />

          <Route path="/team" element={<TeamDashboardIndexPage />} />
          <Route path="/team/:teamSlug" element={<TeamDashboardPage />} />

          <Route path="/explore" element={<ExploreLayout />}>
            <Route index element={<Navigate to="/explore/sales" replace />} />
            <Route path=":departmentId" element={<ExplorePage />} />
          </Route>

          <Route path="/targets" element={<TargetLayout />}>
            <Route index element={<Navigate to="/targets/sales" replace />} />
            <Route path=":departmentId" element={<TargetEditorPage />} />
          </Route>

          <Route path="/targets-test" element={<Navigate to="/targets/sales" replace />} />
          <Route path="/targets-test/:departmentId" element={<LegacyTargetsTestRedirect />} />

          <Route path="/audit" element={<AuditPage />} />

          <Route path="/account/password" element={<ChangePasswordPage />} />

          <Route path="/raw" element={<RawLayout />}>
            <Route index element={<Navigate to={defaultRawRoute} replace />} />
            <Route path="sync-runs" element={<RawDatasetPage datasetId="sync-runs" />} />
            <Route
              path="daily-activities"
              element={<RawDatasetPage datasetId="daily-activities" />}
            />
            <Route path="period-metrics" element={<RawDatasetPage datasetId="period-metrics" />} />
            <Route
              path="team-period-metrics"
              element={<RawDatasetPage datasetId="team-period-metrics" />}
            />
            <Route path="reps" element={<RawDatasetPage datasetId="reps" />} />
            <Route path="teams" element={<RawDatasetPage datasetId="teams" />} />
          </Route>

          <Route element={<RequireAdmin />}>
            <Route path="/admin/invites" element={<AdminInvitesPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/company/sales" replace />} />
    </Routes>
  );
}
